import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useTheme } from '@material-ui/styles';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
    GoBackButton,
    StyledCard,
    StyledCardContent,
    PersonalInfoForm,
    PersonalHealthForm,
    EmergencyForm,
    OutlinedButton,
    ContainedButton,
    ConfirmAccountDialog,
    Text,
    Progress,
} from '../../components/index';

import { providerPageWidth } from '../../constant';

import {
    GET_PATIENT,
    UPDATE_PATIENT,
    addPatientByStaff,
    checkExistHNCode,
    checkExistUser,
    addExistUserToPatient,
} from '../../services/provider';

import { alertError, alertSuccess } from '../../redux/stateActions';

import {
    validEmail,
    validPhone,
    validAlphabetAndNumber,
    specialCharacters,
} from '../../utils/regex';

import checkID from '../../utils/checkID';

const AddPatient = ({ history, location }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const theme = useTheme();
    const user = useSelector((state) => state.user);

    const [isDisabled, setIsDisabled] = useState(false);

    const [showDialog, setShowDialog] = useState(false);

    const [loading, setLoading] = useState(false);

    /*--------------- Personal Info Form ---------------*/
    const [errors, setErrors] = useState([]);
    const [personalInfo, setPersonalInfo] = useState({
        HNCode: '',
        IDCardNumber: '',
        fullname: '',
        gender: '',
        email: '',
        mobileNumber: '',
        birthday: null,
    });

    const handlePersonalChange = (event) => {
        let name = event.target.name;
        let value = event.target.value;

        if (name === 'HNCode' && value !== '' && !validAlphabetAndNumber.test(value)) {
            return;
        }

        let newPersonalInfo = { ...personalInfo };
        newPersonalInfo[name] = value;
        setPersonalInfo(newPersonalInfo);

        setErrors(errors.filter((error) => error.property !== name));

        if (name === 'IDCardNumber') {
            setShowAddByID(false);
        }
        if (name === 'email') {
            setShowAddByEmail(false);
        }
        if (name === 'mobileNumber') {
            setShowAddByMobile(false);
        }
    };

    const handleDateChange = (event) => {
        let newErrors = [...errors];

        let newPersonalInfo = { ...personalInfo };

        if (event === null) {
            newPersonalInfo.birthday = undefined;
            setPersonalInfo(newPersonalInfo);

            newErrors = newErrors.filter((error) => error.property !== 'birthday');
        } else if (dayjs(event).isValid()) {
            newPersonalInfo.birthday = event.toISOString();
            setPersonalInfo(newPersonalInfo);

            newErrors = newErrors.filter((error) => error.property !== 'birthday');
        } else {
            newErrors.push({
                property: 'birthday',
                message: 'Invalid date.',
            });
        }

        setErrors(newErrors);
    };

    /*--------------- Personal Health Form ---------------*/
    const [personalHealth, setPersonalHealth] = useState({
        weight: 0,
        height: 0,
        drugAllergy: 'none',
        chronicDeseases1: 'none',
        pregnant: false,
        smoker: false,
        drinker: false,
        chronicDeseases2: '',
        familyMedicalRecord: '',
        patientTag: [],
    });

    const handlePersonalHealthChange = (event) => {
        let name = event.target.name;
        let value = event.target.value;

        let newPersonalHealth = { ...personalHealth };
        if (['pregnant', 'smoker', 'drinker'].includes(name)) {
            newPersonalHealth[name] = event.target.checked;
        } else {
            newPersonalHealth[name] = value;
        }

        setPersonalHealth(newPersonalHealth);

        setErrors(errors.filter((error) => error.property !== name));
    };

    const handleAddTag = (value) => {
        if (id) {
            updatePatient({
                variables: {
                    userID: getPatientData?.patient?.user?.id,
                    patientID: id,
                    patientTag: value,
                },
            });
            return;
        } else {
            setPersonalHealth({ ...personalHealth, patientTag: value });
        }
    };

    /*--------------- Emergency Form ---------------*/
    const [emergencyContact, setEmergencyContact] = useState({
        emergencyContractNumber: '',
        emergencyContractName: '',
        emergencyContractRelation: '',
    });

    const handleEmergencyContactChange = (event) => {
        let name = event.target.name;
        let value = event.target.value;

        let newEmergencyContact = { ...emergencyContact };
        newEmergencyContact[name] = value;
        setEmergencyContact(newEmergencyContact);

        setErrors(errors.filter((error) => error.property !== name));
    };

    /*--------------- Get Patient ---------------*/
    const {
        data: getPatientData,
        error: getPatientError,
        loading: getPatientLoading,
        refetch: getPatientRefetch,
    } = useQuery(GET_PATIENT, {
        variables: { id: id },
        skip: !id,
    });

    const setPatient = () => {
        const {
            user,
            weight,
            height,
            drugAllergy,
            chronicDeseases1,
            chronicDeseases2,
            pregnant,
            smoker,
            drinker,
            familyMedicalRecord,
            emergencyContractNumber,
            emergencyContractName,
            emergencyContractRelation,
            HNCode,
            patientTag,
        } = getPatientData.patient;

        if (_.isNull(user)) {
            setIsDisabled(true);
        }

        setPersonalInfo({
            HNCode: HNCode || '',
            IDCardNumber: user?.IDCardNumber || '',
            fullname: user?.fullname || '',
            gender: user?.gender || '',
            email: user?.email || '',
            mobileNumber: user?.mobileNumber || '',
            birthday: user?.birthday || null,
        });

        setPersonalHealth({
            weight: weight || 0,
            height: height || 0,
            drugAllergy: drugAllergy || 'none',
            chronicDeseases1: chronicDeseases1 || 'none',
            pregnant: pregnant || false,
            smoker: smoker || false,
            drinker: drinker || false,
            chronicDeseases2: chronicDeseases2 || '',
            familyMedicalRecord: familyMedicalRecord || '',
            patientTag: patientTag || [],
        });

        setEmergencyContact({
            emergencyContractNumber: emergencyContractNumber || '',
            emergencyContractName: emergencyContractName || '',
            emergencyContractRelation: emergencyContractRelation || '',
        });
    };

    useEffect(() => {
        if (getPatientError) {
            dispatch(alertError(`Cannot get Client: ${getPatientError.message}`));
            history.goBack();
            return;
        }

        if (getPatientData && getPatientData.patient === null) {
            dispatch(alertError(`Client not found`));
            history.goBack();
            return;
        }

        if (getPatientData && getPatientData.patient) {
            setPatient();
        }
    }, [getPatientError, getPatientData]);

    /*--------------- Update Patient ---------------*/
    const [
        updatePatient,
        { data: updatePatientData, error: updatePatientError, loading: updatePatientLoading },
    ] = useMutation(UPDATE_PATIENT);

    useEffect(() => {
        if (updatePatientError) {
            dispatch(alertError(`Cannot update Client: ${updatePatientError.message}`));
        }
        if (updatePatientData) {
            dispatch(alertSuccess('Client updated'));
            getPatientRefetch();
        }
    }, [updatePatientError, updatePatientData]);

    const handleClear = () => {
        setErrors([]);
        setShowAddByID(false);
        setShowAddByEmail(false);
        setShowAddByMobile(false);

        if (id) {
            setPatient();
            return;
        }

        setPersonalInfo({
            HNCode: '',
            IDCardNumber: '',
            fullname: '',
            gender: '',
            email: '',
            mobileNumber: '',
            birthday: null,
        });

        setPersonalHealth({
            weight: 0,
            height: 0,
            drugAllergy: 'none',
            chronicDeseases1: 'none',
            pregnant: false,
            smoker: false,
            drinker: false,
            chronicDeseases2: '',
            familyMedicalRecord: '',
            patientTag: [],
        });

        setEmergencyContact({
            emergencyContractNumber: '',
            emergencyContractName: '',
            emergencyContractRelation: '',
        });
    };

    const makeVariables = () => {
        const variables = {
            ...personalInfo,
            ...personalHealth,
            ...emergencyContact,
            height: parseFloat(personalHealth.height),
            weight: parseFloat(personalHealth.weight),
            birthday: personalInfo.birthday
                ? dayjs(personalInfo.birthday).format('YYYY-MM-DD')
                : null,
        };
        return variables;
    };

    const handleAddExistUserToPatient = async (type) => {
        setLoading(true);
        try {
            const data = {
                ...personalInfo,
                ...personalHealth,
                ...emergencyContact,
                height: parseFloat(personalHealth.height),
                weight: parseFloat(personalHealth.weight),
                hospitalID: user.staff.hospital.id,
            };

            if (type === 'email') {
                delete data.mobileNumber;
                delete data.IDCardNumber;
            }
            if (type === 'mobileNumber') {
                delete data.email;
                delete data.IDCardNumber;
            }
            if (type === 'id') {
                delete data.email;
                delete data.mobileNumber;
            }

            const res = await addExistUserToPatient(data);

            if (res.status === 200) {
                dispatch(alertSuccess('client Added'));
                history.push(`/provider/client`);
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);
            const newErrors = [...errors];
            const { message } = error.response.data;
            dispatch(alertError(message));
            if (message === 'HN Code already taken') {
                newErrors.push({
                    property: 'HNCode',
                    message: 'HN Code already taken',
                });
            }
            if (!_.isEmpty(newErrors)) return setErrors(newErrors);
        }
        setLoading(false);
    };

    /*--------------- Check ID exist ---------------*/
    const [showAddByID, setShowAddByID] = useState(false);
    /*--------------- Check Email exist ---------------*/
    const [showAddByEmail, setShowAddByEmail] = useState(false);
    /*--------------- Check Mobile Number exist ---------------*/
    const [showAddByMobile, setShowAddByMobile] = useState(false);

    const handleCheckExistUserWIthEmailMobile = async (data) => {
        try {
            setLoading(true);
            const res = await checkExistUser(data);

            if (res.status === 200) {
                setLoading(false);

                return res.data;
            } else {
                throw new Error(res);
            }
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Check exist user: ${error.response?.data?.message}`));
        }
    };

    const handleSubmit = async () => {
        const newErrors = [...errors];

        if (personalInfo.HNCode !== '') {
            try {
                setLoading(true);

                const res = await checkExistHNCode({
                    HNCode: personalInfo.HNCode,
                    hospitalID: user.staff.hospital.id,
                    patientID: id,
                });

                if (res.status === 200) {
                    const { message } = res.data;

                    // found user with this ID card number and already patient in this hospital
                    if (message === 'patientFound') {
                        newErrors.push({
                            property: 'HNCode',
                            message: 'HNCode already Exit',
                        });
                    }

                    setLoading(false);
                } else {
                    throw new Error(res);
                }
            } catch (error) {
                dispatch(alertError(`Check exist ID Code: ${error.response?.data?.message}`));
                setLoading(false);
            }
        }

        if (!checkID(personalInfo.IDCardNumber)) {
            newErrors.push({
                property: 'IDCardNumber',
                message: 'Please provide a valid ID Card Number.',
            });
        } else {
            setShowAddByID(false);

            const checkEmailRes = await handleCheckExistUserWIthEmailMobile({
                email: personalInfo.email,
                userID: id ? getPatientData?.patient?.user?.id : undefined,
                role: 'patient',
                hospital: user.staff.hospital.id,
            });

            switch (checkEmailRes) {
                case 'userNotFound':
                    break;
                case 'isThisUser':
                    break;
                case 'userFound':
                    newErrors.push({
                        property: 'IDCardNumber',
                        message: 'ID Card Number already exists.',
                    });
                    if (!id) {
                        setShowAddByID(true);
                    }
                    break;
                case 'staffFound':
                    newErrors.push({
                        property: 'IDCardNumber',
                        message: 'Client already in Counseling Service.',
                    });
                    break;
            }
        }

        if (personalInfo.fullname === '') {
            newErrors.push({
                property: 'fullname',
                message: 'Please provide a valid Fullname.',
            });
        }

        if (specialCharacters.test(personalInfo.fullname)) {
            newErrors.push({
                property: 'fullname',
                message: 'Fullname must not include special characters.',
            });
        }

        if (personalInfo.gender === '') {
            newErrors.push({
                property: 'gender',
                message: 'Please provide a valid gender.',
            });
        }

        if (!validEmail.test(personalInfo.email)) {
            newErrors.push({
                property: 'email',
                message: 'Please provide a valid Email.',
            });
        } else {
            setShowAddByEmail(false);

            const checkEmailRes = await handleCheckExistUserWIthEmailMobile({
                email: personalInfo.email,
                userID: id ? getPatientData?.patient?.user?.id : undefined,
                role: 'patient',
                hospital: user?.staff?.hospital.id,
            });

            switch (checkEmailRes) {
                case 'userNotFound':
                    break;
                case 'isThisUser':
                    break;
                case 'userFound':
                    newErrors.push({
                        property: 'email',
                        message: 'Email already exists.',
                    });
                    if (!id) {
                        setShowAddByEmail(true);
                    }
                    break;
                case 'staffFound':
                    newErrors.push({
                        property: 'email',
                        message: 'Client already in Counseling Service.',
                    });
                    break;
            }
        }

        if (!validPhone.test(personalInfo.mobileNumber)) {
            newErrors.push({
                property: 'mobileNumber',
                message: 'Please provide a valid Mobile Number.',
            });
        } else {
            setShowAddByMobile(false);

            const checkMobileRes = await handleCheckExistUserWIthEmailMobile({
                mobileNumber: personalInfo.mobileNumber,
                userID: id ? getPatientData?.patient?.user?.id : undefined,
                role: 'patient',
                hospital: user?.staff?.hospital.id,
            });

            switch (checkMobileRes) {
                case 'userNotFound':
                    break;
                case 'isThisUser':
                    break;
                case 'userFound':
                    newErrors.push({
                        property: 'mobileNumber',
                        message: 'Mobile Number already exists.',
                    });
                    if (!id) {
                        setShowAddByMobile(true);
                    }
                    break;
                case 'staffFound':
                    newErrors.push({
                        property: 'mobileNumber',
                        message: 'Client already in Counseling Service.',
                    });
                    break;
            }
        }

        if (!personalHealth.weight || personalHealth.weight == 0 || personalHealth.weight === '') {
            newErrors.push({
                property: 'weight',
                message: 'Please provide a valid weight.',
            });
        }

        if (!personalHealth.height || personalHealth.height == 0 || personalHealth.height === '') {
            newErrors.push({
                property: 'height',
                message: 'Please provide a valid height.',
            });
        }


        if (
            emergencyContact.emergencyContractNumber === '' ||
            !validPhone.test(emergencyContact.emergencyContractNumber)
        ) {
            newErrors.push({
                property: 'emergencyContractNumber',
                message: 'Please provide a valid Contact Number.',
            });
        }

        if(!emergencyContact.emergencyContractName){
            newErrors.push({
                property: 'emergencyContractName',
                message: 'Please provide a valid Contact Name.',
            });
        }

        if(!emergencyContact.emergencyContractRelation){
            newErrors.push({
                property: 'emergencyContractRelation',
                message: 'Please provide a valid Relationship.',
            });
        }

        if (!_.isEmpty(newErrors)) return setErrors(newErrors);

        if (!id) {
            return setShowDialog(true);
        }

        updatePatient({
            variables: {
                userID: getPatientData?.patient?.user?.id,
                patientID: id,
                ...makeVariables(),
            },
        });
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    const handleCreateAccount = async () => {
        setLoading(true);
        setShowDialog(false);

        try {
            const res = await addPatientByStaff({
                hospitalID: user.staff.hospital.id,
                ...makeVariables(),
            });

            if (!res) {
                throw new Error(res);
            }

            dispatch(alertSuccess('Create new client successful!'));
            history.push('/provider/client');
        } catch (error) {
            setLoading(false);
            dispatch(alertError(`Cannt add Client: ${error.response?.data?.message}`));
        }
    };

    const handleGoBack = () => {
        if (location.state?.prevPath) {
            const path = {
                pathname: location.state.prevPath,
                state: {
                    tab: location.state.prevTab,
                },
            };
            history.push(path);
        } else {
            history.goBack();
        }
    };

    return (
        <div
            style={{
                padding: '41px 20px 90px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '20px',
            }}
        >
            <ConfirmAccountDialog
                open={showDialog}
                onClose={handleCloseDialog}
                onConfirm={handleCreateAccount}
            />

            {location.state?.prevPath && <GoBackButton $text onClick={handleGoBack} />}
            <Text $bold $fontSize="24px">
                {id ? 'Edit' : 'Add new'} client
            </Text>
            <StyledCard variant="outlined" padding="20px">
                {(loading || getPatientLoading || updatePatientLoading) && <Progress />}
                <StyledCardContent>
                    <PersonalInfoForm
                        data={personalInfo}
                        onChange={handlePersonalChange}
                        onDateChange={handleDateChange}
                        errors={errors}
                        radioRow
                        showAddByID={showAddByID}
                        showAddByEmail={showAddByEmail}
                        showAddByMobile={showAddByMobile}
                        handleAddExistUserToPatient={handleAddExistUserToPatient}
                        disabledForm={isDisabled}
                    />
                </StyledCardContent>
            </StyledCard>

            <StyledCard variant="outlined" padding="20px">
                {(loading || getPatientLoading || updatePatientLoading) && <Progress />}
                <StyledCardContent>
                    <PersonalHealthForm
                        data={personalHealth}
                        onChange={handlePersonalHealthChange}
                        onAddTag={handleAddTag}
                        minRows={2}
                        errors={errors}
                        disabledForm={isDisabled}
                    />
                </StyledCardContent>
            </StyledCard>

            <StyledCard variant="outlined" padding="20px">
                {(loading || getPatientLoading || updatePatientLoading) && <Progress />}
                <StyledCardContent>
                    <EmergencyForm
                        data={emergencyContact}
                        onChange={handleEmergencyContactChange}
                        errors={errors}
                        disabledForm={isDisabled}
                    />
                </StyledCardContent>
            </StyledCard>
            <div
                style={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    columnGap: '20px',
                    backgroundColor: theme.palette.common.white,
                    opacity: 0.9,
                    border: `1px solid ${theme.palette.common.white}`,
                    boxSizing:
                        '0px -2px 4px rgba(174, 180, 210, 0.4), inset 0px -2px 4px rgba(174, 180, 210, 0.2)',
                    padding: '15px 24px',
                    height: '64px',
                }}
            >
                <OutlinedButton
                    $width="90px"
                    $height="32px"
                    $noShadow
                    onClick={handleClear}
                    disabled={getPatientLoading || updatePatientLoading || isDisabled}
                >
                    Clear
                </OutlinedButton>
                <ContainedButton
                    $width="auto"
                    $height="32px"
                    $noShadow
                    onClick={handleSubmit}
                    disabled={loading || getPatientLoading || updatePatientLoading || isDisabled}
                >
                    {id ? 'Save' : 'Create new client'}
                </ContainedButton>
            </div>
        </div>
    );
};

AddPatient.propTypes = {
    history: PropTypes.object,
    location: PropTypes.object,
};

export default AddPatient;
